/**
 * @file Routines.tsx
 * @description "Rutyny" — habit list for the life layer (GET/POST/PATCH
 * /api/routines). Each routine shows a today-checkbox, a 14-day mini heatmap
 * (checked = emerald, missed = muted), the current streak (flame badge) and
 * its time of day; below sits the new-routine form (name, time of day,
 * specific time, domain, ONGOING/FIXED mode with days count, notify flag).
 * Polled every 30 seconds like Inbox.tsx. All page copy is intentionally
 * Polish (routines i18n namespace) — same convention as Inbox.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Flame, Plus, RefreshCw, Repeat } from "lucide-react";
import { api, LIFE_DOMAINS, LIFE_TIMES_OF_DAY } from "../lib/api";
import type { LifeRoutineEnriched, LifeRoutineMode, LifeRoutineTimeOfDay } from "../lib/api";
import { domainMap } from "../lib/life";
import { EmptyState } from "../components/EmptyState";

const POLL_INTERVAL_MS = 30_000;

interface NewRoutineDraft {
  name: string;
  timeOfDay: LifeRoutineTimeOfDay;
  specificTime: string; // "HH:MM" or ""
  domain: string; // "" = none
  mode: LifeRoutineMode;
  daysCount: string; // numeric input value, only for FIXED
  notify: boolean;
}

const EMPTY_DRAFT: NewRoutineDraft = {
  name: "",
  timeOfDay: "ANYTIME",
  specificTime: "",
  domain: "",
  mode: "ONGOING",
  daysCount: "",
  notify: false,
};

function Heatmap({ last14 }: { last14: boolean[] }) {
  const { t } = useTranslation("routines");
  return (
    <div className="flex items-center gap-0.5" title={t("heatmapLegend")} aria-hidden="true">
      {last14.map((checked, i) => (
        <span
          key={i}
          className={`w-2.5 h-2.5 rounded-sm ${checked ? "bg-emerald-500" : "bg-surface-3"}`}
        />
      ))}
    </div>
  );
}

export function Routines() {
  const { t } = useTranslation("routines");
  const [routines, setRoutines] = useState<LifeRoutineEnriched[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<NewRoutineDraft>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);

  const domains = useMemo(() => domainMap(LIFE_DOMAINS), []);

  const load = useCallback(async () => {
    try {
      const res = await api.routines.list();
      setRoutines(res.routines);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const onToggleToday = useCallback(
    async (id: string) => {
      setBusyId(id);
      setActionError(null);
      try {
        await api.routines.toggleCheck(id);
        await load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const onCreate = useCallback(async () => {
    if (!draft.name.trim()) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const daysCount =
        draft.mode === "FIXED" && draft.daysCount ? Number.parseInt(draft.daysCount, 10) : null;
      await api.routines.create({
        name: draft.name.trim(),
        timeOfDay: draft.timeOfDay,
        specificTime: draft.specificTime || null,
        domain: draft.domain || null,
        mode: draft.mode,
        daysCount: Number.isInteger(daysCount) && (daysCount as number) > 0 ? daysCount : null,
        notify: draft.notify,
      });
      setDraft(EMPTY_DRAFT);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [draft, load]);

  const fieldClass =
    "mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200";
  const labelClass = "block text-[10px] font-semibold uppercase tracking-wider text-gray-500";

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
            <Repeat className="w-4.5 h-4.5 text-accent" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-100">{t("title")}</h1>
            <p className="text-xs text-gray-500">{t("subtitle")}</p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t("common:refresh")}
        </button>
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="card p-3 flex items-center gap-3 border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-gray-300 min-w-0 truncate">
            {t("actionError")}: <span className="font-mono text-gray-500">{actionError}</span>
          </p>
        </div>
      )}

      {/* Error state (endpoint unreachable) */}
      {error && !routines && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">{t("loadError")}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !routines && !error && (
        <div className="space-y-3 max-w-3xl">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse h-16 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      <div className="max-w-3xl space-y-8">
        {/* Routine list */}
        {routines && routines.length > 0 && (
          <div className="space-y-2">
            {routines.map((routine) => {
              const domain = routine.domain ? domains.get(routine.domain) : undefined;
              return (
                <div key={routine.id} className="card-hover p-3 flex items-center gap-3">
                  <button
                    onClick={() => onToggleToday(routine.id)}
                    disabled={busyId === routine.id}
                    title={t("checkToday")}
                    aria-label={t("checkToday")}
                    className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-60 ${
                      routine.checkedToday
                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                        : "border-border bg-surface-2 text-transparent hover:border-emerald-500/40"
                    }`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-200 truncate">{routine.name}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-400">
                      <span>
                        {t(`timeOfDayNames.${routine.time_of_day}`)}
                        {routine.specific_time ? ` · ${routine.specific_time}` : ""}
                      </span>
                      {routine.mode === "FIXED" && routine.days_count != null && (
                        <span className="text-gray-500">
                          {t("fixedBadge", { count: routine.days_count })}
                        </span>
                      )}
                      {domain && (
                        <span className="inline-flex items-center gap-1">
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: domain.color }}
                            aria-hidden="true"
                          />
                          {domain.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Heatmap last14={routine.last14} />
                    {routine.streak > 0 && (
                      <span className="badge bg-orange-500/10 text-orange-400 border-orange-500/20 inline-flex items-center gap-1">
                        <Flame className="w-3 h-3" aria-hidden />
                        {routine.streak}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {routines && routines.length === 0 && (
          <EmptyState icon={Repeat} title={t("emptyTitle")} description={t("emptyDescription")} />
        )}

        {/* New-routine form */}
        <div className="card p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-100">{t("formTitle")}</h2>
          <label className={labelClass}>
            {t("fieldName")}
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreate();
              }}
              className={fieldClass}
            />
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className={labelClass}>
              {t("fieldTimeOfDay")}
              <select
                value={draft.timeOfDay}
                onChange={(e) =>
                  setDraft({ ...draft, timeOfDay: e.target.value as LifeRoutineTimeOfDay })
                }
                className={fieldClass}
              >
                {LIFE_TIMES_OF_DAY.map((tod) => (
                  <option key={tod} value={tod}>
                    {t(`timeOfDayNames.${tod}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              {t("fieldTime")}
              <input
                type="time"
                value={draft.specificTime}
                onChange={(e) => setDraft({ ...draft, specificTime: e.target.value })}
                className={fieldClass}
              />
            </label>
            <label className={labelClass}>
              {t("fieldDomain")}
              <select
                value={draft.domain}
                onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                className={fieldClass}
              >
                <option value="">{t("noDomain")}</option>
                {LIFE_DOMAINS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              {t("fieldMode")}
              <select
                value={draft.mode}
                onChange={(e) => setDraft({ ...draft, mode: e.target.value as LifeRoutineMode })}
                className={fieldClass}
              >
                <option value="ONGOING">{t("modeNames.ONGOING")}</option>
                <option value="FIXED">{t("modeNames.FIXED")}</option>
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            {draft.mode === "FIXED" && (
              <label className={labelClass}>
                {t("fieldDaysCount")}
                <input
                  type="number"
                  min={1}
                  value={draft.daysCount}
                  onChange={(e) => setDraft({ ...draft, daysCount: e.target.value })}
                  className={`${fieldClass} w-24`}
                />
              </label>
            )}
            <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer py-1.5">
              <input
                type="checkbox"
                checked={draft.notify}
                onChange={(e) => setDraft({ ...draft, notify: e.target.checked })}
                className="rounded border-border bg-surface-1"
              />
              {t("fieldNotify")}
            </label>
          </div>
          <button
            onClick={onCreate}
            disabled={submitting || !draft.name.trim()}
            className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("actionAdd")}
          </button>
        </div>
      </div>
    </div>
  );
}
